import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import AppContext from '../context/AppContext';
import AuthAPI from '../api/AuthAPI';

// The React login overlay: the /Identity/Account/Login flow without leaving the page. On success it
// reloads so the server re-renders the authenticated chrome.
export default function LoginModal(): React.ReactNode {
  const { showLogin, closeLogin, googleEnabled } = useContext(AppContext);

  const [step, setStep] = useState<'email' | 'code' | 'password'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset to a clean state each time the overlay opens.
  useEffect(() => {
    if (showLogin) {
      setStep('email');
      setCode('');
      setPassword('');
      setError(null);
      setBusy(false);
    }
  }, [showLogin]);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await AuthAPI.requestCode(email);
      setStep('code');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await AuthAPI.verifyCode(email, code);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  const passwordLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await AuthAPI.passwordLogin(email, password);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  return (
    <Modal show={showLogin} onHide={closeLogin} centered dialogClassName="sc-login-modal">
      <Modal.Body className="sc-login-body">
        <button type="button" className="sc-login-close" aria-label="Close" onClick={closeLogin}>
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className="sc-login-hero">
          <img
            className="sc-login-badge"
            src="/images/login_icon.png"
            alt=""
            aria-hidden="true"
            width={72}
            height={72}
          />
          <h2 className="sc-login-title">Welcome back</h2>
          <p className="sc-login-subtitle">Save and revisit every game you&apos;ve played.</p>
        </div>

        {step !== 'password' ? (
          <div className="sc-login-note" role="note">
            <strong>No password needed</strong>. We&apos;ll email you a sign-in code.
          </div>
        ) : null}

        {error != null ? (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        ) : null}

        {step === 'email' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendCode();
            }}
          >
            <div className="mb-3">
              <input
                type="email"
                className="form-control"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" variant="primary" className="w-100" disabled={busy}>
              {busy ? 'Sending…' : 'Continue'}
            </Button>

            <div className="text-center mt-2">
              <Button
                variant="link"
                className="p-0"
                disabled={busy}
                onClick={() => {
                  setStep('password');
                  setError(null);
                }}
              >
                Log in with a password instead
              </Button>
            </div>
          </form>
        ) : step === 'code' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verifyCode();
            }}
          >
            <p className="text-muted">
              We emailed a code to <strong>{email}</strong>. Enter it below to log in.
            </p>
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <Button type="submit" variant="primary" className="w-100" disabled={busy}>
              {busy ? 'Logging in…' : 'Log in'}
            </Button>
            <Button
              variant="link"
              className="p-0 mt-2"
              disabled={busy}
              onClick={() => {
                setStep('email');
                setError(null);
              }}
            >
              Use a different email
            </Button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              passwordLogin();
            }}
          >
            <p className="text-muted">Enter your email and password to log in.</p>
            <div className="mb-3">
              <input
                type="email"
                className="form-control"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" variant="primary" className="w-100" disabled={busy}>
              {busy ? 'Logging in…' : 'Log in'}
            </Button>
            <div className="text-center mt-2">
              <Button
                variant="link"
                className="p-0"
                disabled={busy}
                onClick={() => {
                  setStep('email');
                  setError(null);
                }}
              >
                Use a login code instead
              </Button>
            </div>
          </form>
        )}

        {googleEnabled && step !== 'code' ? (
          <>
            <div className="text-center text-muted my-2">or</div>
            <a
              href={AuthAPI.googleUrl()}
              className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2582h2.9082c1.7018-1.5668 2.6841-3.874 2.6841-6.6151z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.4673-.806 5.9564-2.1809l-2.9082-2.2582c-.8059.54-1.8368.859-3.0482.859-2.344 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
                />
              </svg>
              Continue with Google
            </a>
          </>
        ) : null}

        <p className="sc-login-terms">
          By continuing you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms &amp; Privacy Policy
          </a>
          .
        </p>
      </Modal.Body>
    </Modal>
  );
}
